import { Edges, RoundedBox } from '@react-three/drei'
import type { AncoraOcupante, PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import {
  RECLINACAO_ENCOSTO_RAD,
  RECUO_TRILHO,
  centrosDoBanco,
  larguraDoBanco,
  medidasDaAncora,
} from '../../dominio/didatico/bancos'

export interface EstadoBancoAnterior {
  reclinado: boolean
  recuado: boolean
  apoioCabecaVisivel: boolean
}

// Mesmo âmbar que `CenaExtracao` usa para a rota comparada: o banco de fábrica
// (0.92 de opacidade, tom frio) não contrasta com um fantasma frio parecido —
// o âmbar já é a cor que o aluno associa a "isto é uma referência, não a peça real".
const COR_FANTASMA = '#ffc44d'
const OPACIDADE_FANTASMA = 0.35

/**
 * Silhueta da posição ANTERIOR do banco (Fase 4): mostra de onde a peça
 * saiu, para comparar com a posição atual que `Habitaculo` já desenha.
 *
 * Mesma geometria e as mesmas medidas de `Habitaculo.tsx` — a `Banco` real e
 * esta silhueta usam `centrosDoBanco`/`medidasDaAncora`, não um desenho à
 * parte. A diferença é só de estado (o "antes" em vez do estado atual do
 * contexto) e de material (fantasma, translúcido).
 *
 * Deliberadamente sem `<Peca>`: não tem `onPointerDown`, não entra na
 * seleção e não é lida por `estadoPecas`/`obstaculosDaRota` — puramente
 * decorativa, do jeito que `fantasma` em `VitimaProcedural` já funciona.
 */
export function SilhuetaBancoAnterior({
  perfil,
  ancora,
  estado,
}: {
  perfil: PerfilVeiculo
  ancora: AncoraOcupante
  estado: EstadoBancoAnterior
}) {
  const m = medidasDaAncora(ancora)
  const largura = larguraDoBanco(perfil, ancora)
  const centros = centrosDoBanco(ancora, estado.reclinado)
  const anguloEncosto = m.encosto.inclinacaoRad + (estado.reclinado ? -RECLINACAO_ENCOSTO_RAD : 0)

  const material = (
    <meshStandardMaterial color={COR_FANTASMA} transparent opacity={OPACIDADE_FANTASMA} depthWrite={false} />
  )

  return (
    <group>
      <RoundedBox
        args={[largura, m.assento.altura, m.assento.profundidade]}
        radius={0.04}
        smoothness={2}
        position={[
          centros.assento.x,
          centros.assento.y,
          centros.assento.z - (estado.recuado ? RECUO_TRILHO : 0),
        ]}
      >
        {material}
        <Edges color={COR_FANTASMA} />
      </RoundedBox>

      <group
        position={[centros.encosto.x, ancora.alturaAssento, centros.encosto.z]}
        rotation={[anguloEncosto, 0, 0]}
      >
        <RoundedBox
          args={[largura - 0.02, m.encosto.altura, m.encosto.espessura]}
          radius={0.04}
          smoothness={2}
          position={[0, m.encosto.offsetY, 0]}
        >
          {material}
          <Edges color={COR_FANTASMA} />
        </RoundedBox>

        {estado.apoioCabecaVisivel && (
          <RoundedBox
            args={[m.apoioCabeca.largura, m.apoioCabeca.altura, m.apoioCabeca.espessura]}
            radius={0.04}
            smoothness={2}
            position={[0, m.apoioCabeca.offsetY, m.apoioCabeca.offsetZ - m.encosto.offsetZ]}
          >
            {material}
            <Edges color={COR_FANTASMA} />
          </RoundedBox>
        )}
      </group>
    </group>
  )
}
